import { Anchor, Group } from "@mantine/core";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { generatePath, Link } from "react-router";
import { ROUTES } from "../../../../routes";
import { TodoStatus, type Todo } from "../../../../utils/backend-types";
import { LAST_YEAR } from "../../../../utils/dates";
import { StatusSymbol, StatusSymbolColor } from "../../../Icons/StatusSymbol";
import { OnboardingTodo } from "../../../Todos/Todos.constants";
import { getOnboardingTodoLabel } from "../../../Todos/utils/getOnboardingTodoLabel";
import type { SiteOverviewSite, SiteStatusSite } from "../../site-status-types";

export const NO_TODOS_TEXT = "Keine Aufgaben";
export const ALL_DONE_TEXT = "Onboarding abgeschlossen";

export const RELEVANT_ONBOARDING_TODOS = [
  OnboardingTodo.KONFIGURATION_PRUEFEN,
  OnboardingTodo.REGULATORISCHE_DATEN_VERVOLLSTAENDIGEN,
  OnboardingTodo.P6_VERTRAEGE_HINTERLEGEN,
  OnboardingTodo.DIREKTVERMARKTUNGSVERTRAEGE_HINTERLEGEN,
  OnboardingTodo.VOLLMACHT_FUER_MSB_BEREITSTELLEN,
  OnboardingTodo.VOLLMACHT_FUER_VERMARKTER_BEREITSTELLEN
] as const;

function isDone(todos: Array<Todo>, todoType: OnboardingTodo): boolean {
  const todo = todos.find(
    (t) => t.label === getOnboardingTodoLabel(todoType, LAST_YEAR)
  );

  if (todo === undefined) return true;
  return (
    todo.status === TodoStatus.Done || todo.status === TodoStatus.NotRelevant
  );
}

function getNextTodoIndex(todos: Array<Todo>): number {
  const allDone = RELEVANT_ONBOARDING_TODOS.every((t) => isDone(todos, t));
  if (allDone) return RELEVANT_ONBOARDING_TODOS.length;
  return RELEVANT_ONBOARDING_TODOS.findIndex((t) => !isDone(todos, t));
}

function sortOnboardingTodosSimple(a: Array<Todo>, b: Array<Todo>) {
  return getNextTodoIndex(a) - getNextTodoIndex(b);
}

function getOnboardingColumn(): MRT_ColumnDef<
  SiteOverviewSite | SiteStatusSite
> {
  return {
    header: "Nächste Onboarding-Aufgabe",
    accessorKey: "onboardingTodos",
    size: 315,
    Cell: ({ cell, row }) => (
      <OnboardingTodosCellSimple
        projectId={row.original.projectId}
        siteId={row.original.id}
        todos={cell.getValue<Array<Todo>>()}
      />
    ),
    enableGlobalFilter: false,
    sortingFn: (rowA, rowB) =>
      sortOnboardingTodosSimple(
        rowA.getValue("onboardingTodos"),
        rowB.getValue("onboardingTodos")
      ),
    filterVariant: "multi-select",
    mantineFilterMultiSelectProps: {
      data: [
        ...RELEVANT_ONBOARDING_TODOS.map((todoType) => ({
          value: todoType.toString(),
          label: getOnboardingTodoLabel(todoType, LAST_YEAR)
        })),
        { value: ALL_DONE_TEXT, label: ALL_DONE_TEXT }
      ]
    },
    filterFn: (row, _columnId, filterValue) => {
      if (filterValue.length === 0) {
        return true;
      }

      const todos: Array<Todo> = row.getValue("onboardingTodos");
      const allDone = RELEVANT_ONBOARDING_TODOS.every((t) => isDone(todos, t));
      const isAllDoneInFilter = filterValue.includes(ALL_DONE_TEXT);

      if (isAllDoneInFilter && allDone) {
        return true;
      }

      const nextTodo = RELEVANT_ONBOARDING_TODOS.find((t) => !isDone(todos, t));
      const isNextTodoInFilter =
        nextTodo !== undefined
          ? filterValue.includes(nextTodo.toString())
          : false;
      return isNextTodoInFilter;
    }
  };
}

interface OnboardingTodosCellSimpleProps {
  todos: Array<Todo>;
  projectId: string;
  siteId: number;
}

function OnboardingTodosCellSimple({
  todos,
  projectId,
  siteId
}: OnboardingTodosCellSimpleProps) {
  const numTodos = todos.length;

  if (numTodos === 0) {
    return (
      <Group gap={0}>
        <StatusSymbol noStatus />
        {NO_TODOS_TEXT}
      </Group>
    );
  }

  const nextTodo = RELEVANT_ONBOARDING_TODOS.find((t) => !isDone(todos, t));
  const allDone = nextTodo === undefined;
  const displayText = allDone
    ? ALL_DONE_TEXT
    : getOnboardingTodoLabel(nextTodo, LAST_YEAR);
  const managerVariantStructurePath = generatePath(ROUTES.site.entry, {
    projectId: projectId,
    siteId: siteId.toString()
  });

  return (
    <Group gap={0}>
      <StatusSymbol
        color={allDone ? StatusSymbolColor.Green : StatusSymbolColor.Red}
      />
      {allDone ? (
        displayText
      ) : (
        <Anchor component={Link} to={managerVariantStructurePath}>
          {displayText}
        </Anchor>
      )}
    </Group>
  );
}

export { getOnboardingColumn };
